import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useSeo } from "@/hooks/useSeo";
import ImageUpload from "@/components/ImageUpload";

const Field = ({ label, children }) => (
  <div>
    <label className="text-sm font-medium text-slate-700">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
    <h2 className="font-heading text-lg font-semibold text-slate-800">{title}</h2>
    {children}
  </div>
);

const AdminPengaturan = () => {
  const { refresh } = useSite();
  const [f, setF] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useSeo("Pengaturan Website");

  useEffect(() => { api.get("/settings").then((r) => setF(r.data || {})).catch(() => {}).finally(() => setLoading(false)); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setNum = (k) => (e) => setF({ ...f, [k]: Number(e.target.value) });
  const setImg = (k) => (v) => setF({ ...f, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", f);
      toast.success("Pengaturan berhasil disimpan");
      refresh();
    } catch { toast.error("Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Pengaturan Website</h1>
          <p className="mt-1 text-sm text-slate-500">Identitas, kontak, statistik, dan media website.</p>
        </div>
        <Button onClick={save} disabled={saving} className="gap-2 bg-emerald-700 hover:bg-emerald-800" data-testid="settings-save-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan
        </Button>
      </div>

      <div className="space-y-6">
        <Section title="Identitas Sekolah">
          <Field label="Nama Sekolah"><Input value={f.school_name || ""} onChange={set("school_name")} data-testid="settings-name-input" /></Field>
          <Field label="Slogan / Tagline"><Input value={f.tagline || ""} onChange={set("tagline")} data-testid="settings-tagline-input" /></Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <ImageUpload label="Logo Sekolah" value={f.logo_url} onChange={setImg("logo_url")} testid="settings-logo" contain hint="Logo resmi tampil di sidebar, footer, halaman login, panel admin & ikon tab browser. Disarankan PNG persegi berlatar transparan (min. 512×512px). Klik Simpan setelah upload." />
            <ImageUpload label="Foto Hero / Banner" value={f.hero_image_url} onChange={setImg("hero_image_url")} testid="settings-hero" />
          </div>
          <Field label="Tentang Sekolah (Ringkas)"><Textarea rows={4} value={f.about_short || ""} onChange={set("about_short")} data-testid="settings-about-input" /></Field>
        </Section>

        <Section title="Kepala Madrasah">
          <ImageUpload label="Foto Kepala Madrasah" value={f.headmaster_photo_url} onChange={setImg("headmaster_photo_url")} testid="settings-headmaster-photo" />
          <Field label="Nama Kepala Madrasah"><Input value={f.headmaster_name || ""} onChange={set("headmaster_name")} data-testid="settings-headmaster-name-input" /></Field>
          <Field label="Sambutan"><Textarea rows={4} value={f.headmaster_message || ""} onChange={set("headmaster_message")} data-testid="settings-headmaster-message-input" /></Field>
        </Section>

        <Section title="Statistik Sekolah">
          <div className="grid gap-5 sm:grid-cols-4">
            <Field label="Jumlah Siswa"><Input type="number" value={f.stat_students ?? ""} onChange={setNum("stat_students")} data-testid="settings-students-input" /></Field>
            <Field label="Jumlah Guru"><Input type="number" value={f.stat_teachers ?? ""} onChange={setNum("stat_teachers")} data-testid="settings-teachers-input" /></Field>
            <Field label="Jumlah Kelas"><Input type="number" value={f.stat_classes ?? ""} onChange={setNum("stat_classes")} data-testid="settings-classes-input" /></Field>
            <Field label="Tahun Berdiri"><Input type="number" value={f.stat_founded ?? ""} onChange={setNum("stat_founded")} data-testid="settings-founded-input" /></Field>
          </div>
        </Section>

        <Section title="Kontak & Media Sosial">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Alamat"><Input value={f.address || ""} onChange={set("address")} data-testid="settings-address-input" /></Field>
            <Field label="Telepon"><Input value={f.phone || ""} onChange={set("phone")} data-testid="settings-phone-input" /></Field>
            <Field label="Email"><Input value={f.email || ""} onChange={set("email")} data-testid="settings-email-input" /></Field>
            <Field label="WhatsApp (cth: 6281234567890)"><Input value={f.whatsapp || ""} onChange={set("whatsapp")} data-testid="settings-whatsapp-input" /></Field>
            <Field label="Jam Operasional"><Input value={f.operational_hours || ""} onChange={set("operational_hours")} /></Field>
            <Field label="Facebook URL"><Input value={f.facebook || ""} onChange={set("facebook")} /></Field>
            <Field label="Instagram URL"><Input value={f.instagram || ""} onChange={set("instagram")} /></Field>
            <Field label="YouTube URL"><Input value={f.youtube || ""} onChange={set("youtube")} /></Field>
          </div>
          <Field label="Google Maps Embed URL"><Textarea rows={2} value={f.maps_embed || ""} onChange={set("maps_embed")} data-testid="settings-maps-input" /></Field>
        </Section>
      </div>
    </div>
  );
};

export default AdminPengaturan;
